import useSWR from 'swr';
import { backendClient } from '@/lib/api-client';

export interface EntityRole {
  role_type: string;
  side_alignment: string;
  role_description: string | null;
}

export interface EntityRelationship {
  relationship_type: string;
  target_entity_id: string;
  target_entity_name: string;
}

export interface CanonicalEntity {
  id: string;
  /** Display name: the most frequently observed name across documents. */
  name: string;
  /** Stored canonical name, which may differ from the display name. */
  canonical_name: string;
  entity_type: 'person' | 'organization' | 'other';
  entity_kind: string;
  description: string | null;
  /** Display aliases: stored aliases plus the canonical name when it differs. */
  aliases: string[];
  /** Raw stored aliases, safe to edit and write back. */
  stored_aliases: string[];
  roles: EntityRole[];
  relationships: EntityRelationship[];
  document_guids: string[];
}

interface EntitiesResponse {
  entities: CanonicalEntity[];
}

export function useEntities(matterGuid: string | undefined) {
  const { data, error, mutate, isLoading } = useSWR<EntitiesResponse>(
    matterGuid ? `/entities/${matterGuid}` : null,
    async (url: string) => {
      const response = await backendClient.get(url);
      return { entities: response.data.entities || [] };
    },
    {
      revalidateOnFocus: false,
      dedupingInterval: 5000,
    }
  );

  return {
    entities: data?.entities || [],
    isLoading,
    isError: error,
    mutate,
  };
}
